import React, { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import {
  Home, Video, Radio, Users, Heart,
  Calendar, Settings, FileText,
  LogOut, Compass, Library, ChevronRight, ChevronLeft, GraduationCap, BookOpen, Vote,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ActiveMeetingProvider } from "../context/ActiveMeetingContext";
import { BABASAHEB_IMG } from "./BabasahebPortrait";
import { AmbientMusicProvider, AmbientControls } from "./AmbientMusic";
import { getDailyQuote } from "../data/ambedkar_quotes";

const NAV = [
  { to: "/platform", label: "Home", icon: Home, end: true },
  { to: "/platform/meetings", label: "Video Meetings", icon: Video },
  { to: "/platform/live", label: "Live Events", icon: Radio },
  { to: "/platform/library", label: "Dhamma Library", icon: Library },
  { to: "/platform/learning", label: "Learning Modules", icon: GraduationCap },
  { to: "/platform/reading-circles", label: "Reading Circles", icon: BookOpen },
  { to: "/platform/elections", label: "Elections & Polls", icon: Vote },
  { to: "/platform/meditation", label: "Meditation Room", icon: Compass },
  { to: "/platform/community", label: "Community", icon: Users },
  { to: "/platform/calendar", label: "Calendar", icon: Calendar },
  { to: "/platform/notes", label: "AI Meeting Notes", icon: FileText },
  { to: "/platform/donations", label: "Donations", icon: Heart },
  { to: "/platform/settings", label: "Settings", icon: Settings },
];

/**
 * Sidebar nav. `compact` (mobile rail mode used inside an active meeting room)
 * collapses each row to a centered icon — no label, no padding bloat — so the
 * Jitsi iframe gets max horizontal real-estate. Desktop ignores `compact`.
 */
function SidebarNav({ compact, onNavigate }) {
  return (
    <nav className="space-y-1" data-testid="platform-sidebar-nav">
      {NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            title={compact ? item.label : undefined}
            data-testid={`platform-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center rounded-lg transition-colors ${
                compact
                  ? "lg:gap-3 lg:px-4 lg:py-2.5 lg:text-sm justify-center lg:justify-start py-2.5 px-2"
                  : "gap-2.5 lg:gap-3 px-3 lg:px-4 py-2 lg:py-2.5 text-[12px] lg:text-sm"
              } ${
                isActive
                  ? "bg-white/10 text-pearl border border-white/15"
                  : "text-pearl/70 hover:bg-white/5 hover:text-pearl"
              }`
            }
          >
            <Icon size={compact ? 18 : 16} className="lg:w-[18px] lg:h-[18px] flex-shrink-0" />
            <span className={compact ? "hidden lg:inline truncate" : "truncate"}>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function PlatformLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0B1F4D] text-pearl/70 text-sm" data-testid="platform-loading">
        Loading your Sangha…
      </div>
    );
  }
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <AmbientMusicProvider>
      <ActiveMeetingProvider user={user}>
        <PlatformLayoutInner user={user} handleLogout={handleLogout} />
      </ActiveMeetingProvider>
    </AmbientMusicProvider>
  );
}

function PlatformLayoutInner({ user, handleLogout }) {
  const location = useLocation();

  // On mobile we always render the sidebar as a thin icon-only rail so the
  // page content has proper breathing room. Tapping the chevron handle slides
  // the full sidebar in as an overlay. Auto-collapses on route change.
  // Desktop (lg+) ignores this entirely — full sidebar everywhere.
  const [railExpanded, setRailExpanded] = useState(false);
  useEffect(() => { setRailExpanded(false); }, [location.pathname]);

  const compact = !railExpanded; // mobile-only, desktop classes neutralise this

  return (
    <div className="h-screen flex bg-[#F5F1E8] text-navy" data-testid="platform-shell">
      {/* Sidebar, always visible. On mobile it normally shows full content
          (~14rem), but inside a meeting room it shrinks to a ~3.25rem icon
          rail so the Jitsi grid gets the full screen. The user can tap the
          chevron to slide the full sidebar back in as an overlay. */}
      <aside
        className={`flex-shrink-0 bg-gradient-to-b from-[#0B1F4D] to-[#1a2f6b] text-pearl flex flex-col transition-[width] duration-200 lg:w-72 ${
          compact ? "w-[3.25rem]" : "w-[14rem]"
        }`}
        data-testid="platform-sidebar"
        data-compact={compact ? "true" : "false"}
      >
        {/* Header, portrait + (optional) wordmark + (optional) quote */}
        <div className={`border-b border-white/10 ${compact ? "px-2 py-3 lg:px-6 lg:pt-6 lg:pb-4" : "px-3 lg:px-6 pt-4 lg:pt-6 pb-3 lg:pb-4"}`}>
          <Link to="/platform" className={`flex items-center ${compact ? "justify-center lg:justify-start lg:gap-3 gap-0" : "gap-2 lg:gap-3"}`}>
            <div
              className={`rounded-full overflow-hidden border-2 border-saffron/40 ring-2 ring-saffron/15 flex-shrink-0 bg-pearl/5 lg:w-12 lg:h-12 ${
                compact ? "w-8 h-8" : "w-9 h-9"
              }`}
              data-testid="platform-babasaheb-portrait"
            >
              <img
                src={BABASAHEB_IMG}
                alt="Babasaheb Dr. B. R. Ambedkar"
                style={{ objectPosition: "50% 22%" }}
                className="w-full h-full object-cover"
              />
            </div>
            <div className={`min-w-0 ${compact ? "hidden lg:block" : ""}`}>
              <div className="font-display text-[12px] lg:text-base text-pearl leading-tight">Ambedkarite Buddhist Global Network UK</div>
              <div className="text-[8px] lg:text-[10px] uppercase tracking-[0.2em] lg:tracking-[0.25em] text-saffron/85 leading-tight mt-0.5">
                Educate · Agitate · Organise
              </div>
            </div>
          </Link>
          <div className={compact ? "hidden lg:block" : ""}>
            <DailyQuoteBlock />
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto py-3 lg:py-4 ${compact ? "px-1 lg:px-3" : "px-2 lg:px-3"}`}>
          <SidebarNav compact={compact} />
        </div>

        {/* Footer, ambient music + profile/logout. Compact-mobile shows only
            the avatar + a small logout icon. */}
        <div className={`border-t border-white/10 lg:px-4 lg:py-4 lg:space-y-3 ${compact ? "px-1.5 py-2 space-y-2" : "px-3 py-3 space-y-2"}`}>
          <div className={compact ? "hidden lg:block" : ""}>
            <AmbientControls />
          </div>
          {user ? (
            <div className={`flex items-center ${compact ? "flex-col gap-2 lg:flex-row lg:justify-between" : "justify-between gap-2"}`}>
              <Link
                to="/profile"
                className={`flex items-center min-w-0 ${compact ? "lg:gap-2 lg:flex-1 justify-center" : "gap-2 flex-1"}`}
                data-testid="platform-profile-link"
                title={compact ? `${user.name || "Member"} — open profile` : undefined}
              >
                <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-saffron/20 border border-saffron/30 flex items-center justify-center text-saffron text-xs lg:text-sm font-medium flex-shrink-0">
                  {(user.name || "U").charAt(0).toUpperCase()}
                </div>
                <div className={`min-w-0 ${compact ? "hidden lg:block" : ""}`}>
                  <div className="text-[12px] lg:text-sm text-pearl truncate">{user.name || "Member"}</div>
                  <div className="text-[9px] lg:text-[10px] uppercase tracking-widest text-pearl/50 truncate">
                    {user.role === "admin" ? "Admin" : user.membership_tier || "Community"}
                  </div>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 text-pearl/60 hover:text-saffron flex-shrink-0"
                title="Logout"
                data-testid="platform-logout-btn"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="block w-full text-center px-4 py-2 bg-saffron/15 border border-saffron/40 text-saffron rounded-md text-sm hover:bg-saffron/25"
              data-testid="platform-login-cta"
            >
              Login to continue
            </Link>
          )}
          <Link
            to="/"
            className={`block text-center text-[9px] lg:text-[11px] uppercase tracking-widest text-pearl/40 hover:text-pearl/70 ${compact ? "hidden lg:block lg:mt-3" : "mt-2 lg:mt-3"}`}
            data-testid="platform-back-to-public"
          >
            ← Back to public site
          </Link>
        </div>
      </aside>

      {/* Mobile-only "expand rail" handle. Always visible while sidebar is in
          compact mode. Tucked subtly against the rail edge. */}
      {!railExpanded && (
        <button
          type="button"
          onClick={() => setRailExpanded(true)}
          aria-label="Expand sidebar"
          data-testid="platform-rail-expand"
          className="lg:hidden fixed left-[3.25rem] top-1/2 -translate-y-1/2 z-30 w-5 h-12 rounded-r-md bg-[#0B1F4D] border border-l-0 border-white/15 text-pearl/70 hover:text-saffron flex items-center justify-center shadow-md"
        >
          <ChevronRight size={14} />
        </button>
      )}

      {/* Mobile-only collapse handle when expanded as overlay */}
      {railExpanded && (
        <>
          <button
            type="button"
            onClick={() => setRailExpanded(false)}
            aria-label="Collapse sidebar"
            data-testid="platform-rail-collapse"
            className="lg:hidden fixed left-[14rem] top-1/2 -translate-y-1/2 z-50 w-5 h-12 rounded-r-md bg-[#0B1F4D] border border-l-0 border-white/15 text-pearl/70 hover:text-saffron flex items-center justify-center shadow-md"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            aria-label="Close sidebar"
            onClick={() => setRailExpanded(false)}
            className="lg:hidden fixed inset-0 z-20 bg-black/40"
          />
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-y-auto" data-testid="platform-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Daily Ambedkar quote — rotates by UTC day-of-year. Sits in the sidebar
 * directly under Babasaheb's portrait. Members see a fresh quote each day.
 */
function DailyQuoteBlock() {
  const quote = getDailyQuote();
  return (
    <div className="mt-3 lg:mt-4 rounded-md bg-white/5 border border-white/10 p-2 lg:p-3" data-testid="daily-quote">
      <div className="text-[8px] lg:text-[9px] uppercase tracking-[0.2em] lg:tracking-[0.25em] text-saffron mb-1 lg:mb-1.5 inline-flex items-center gap-1.5">
        <span className="w-1 h-1 rounded-full bg-saffron broadcast-live-dot" />
        Quote of the day
      </div>
      <p className="text-[10px] lg:text-xs text-pearl/85 italic leading-relaxed line-clamp-4 lg:line-clamp-none">"{quote.text}"</p>
      <p className="mt-1 lg:mt-1.5 text-[9px] lg:text-[10px] text-pearl/45 not-italic truncate">— {quote.source}</p>
    </div>
  );
}
